import React, { useEffect, useRef, useCallback, useState } from 'react';
import '@/assets/styles/views/notifications/notifications.styles.scss';
import { NotificationIconProps, NotificationType } from './notifications.types.ts';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { useApi } from '@/api/useApi.tsx';
import { formatCustomDate } from '@/utility';
import { useMutation, useInfiniteQuery } from 'react-query';
import { useNotificationStore } from '@/zustand/notification/notificationStore.ts';
import BellOff from '@/assets/svg/bell-off.svg';
import { LoadingSpinner } from '@/components';

const NotificationIcon: React.FC<NotificationIconProps> = ({ type }) => {
	const iconMap: Record<NotificationType, JSX.Element> = {
		ANNOUNCEMENT: <i className="icons-bell" />,
		SMS: <i className="icons-calendar" />,
		INFORM: <i className="icons-doping-hafiza" />,
		money: <i className="icons-money" />,
		error: <i className="icons-close" />,
		cancel: <i className="icons-back" />
	};

	return iconMap[type] || iconMap.user;
};

const NotificationsScreen: React.FC = () => {
	const { ExpertNotificationControllerApi } = useApi();
	const updateNotificationCount = useNotificationStore(s => s.setUnreadNotificationCount);
	const observerRef = useRef<IntersectionObserver | null>(null);
	const loadMoreRef = useRef<HTMLDivElement | null>(null);
	const [readNotificationIds, setReadNotificationIds] = useState<Set<string>>(new Set());

	const PAGE_SIZE = 20;

	const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } = useInfiniteQuery(
		[QUERY_KEYS.getNotificationsByExpertId],
		({ pageParam = 0 }) => ExpertNotificationControllerApi.getByExpertId(pageParam, PAGE_SIZE, ['notificationCreatedDate,desc']),
		{
			getNextPageParam: lastPage => {
				const currentPage = lastPage?.data?.data?.number || 0;
				const totalPages = lastPage?.data?.data?.totalPages || 0;
				const isLast = lastPage?.data?.data?.last || false;

				return !isLast && currentPage < totalPages - 1 ? currentPage + 1 : undefined;
			},
			refetchOnWindowFocus: false
		}
	);

	const { mutate: unreadMutation } = useMutation(QUERY_KEYS.setExpertNotificationsAsRead, (id: Array<string>) => ExpertNotificationControllerApi.setExpertNotificationsAsRead(id), {
		retry: false
	});

	const allNotifications = data?.pages.flatMap(page => page?.data?.data?.content || []) || [];

	const handleObserver = useCallback(
		(entries: IntersectionObserverEntry[]) => {
			const target = entries[0];
			if (target.isIntersecting && hasNextPage && !isFetchingNextPage) {
				fetchNextPage();
			}
		},
		[fetchNextPage, hasNextPage, isFetchingNextPage]
	);

	useEffect(() => {
		const option = {
			root: null,
			rootMargin: '20px',
			threshold: 0.1
		};

		observerRef.current = new IntersectionObserver(handleObserver, option);

		if (loadMoreRef.current) {
			observerRef.current.observe(loadMoreRef.current);
		}

		return () => {
			if (observerRef.current) {
				observerRef.current.disconnect();
			}
		};
	}, [handleObserver]);

	useEffect(() => {
		if (data?.pages?.[0]) {
			const firstPageNotifications = data.pages[0]?.data?.data?.content || [];
			const unreadNotifications = firstPageNotifications
				.filter(n => !n?.readDate && !readNotificationIds.has(n?.id))
				.map(n => n?.id)
				.filter(Boolean);

			if (unreadNotifications.length > 0) {
				unreadMutation(unreadNotifications, {
					onSuccess: () => {
						setReadNotificationIds(prev => {
							const newSet = new Set(prev);
							unreadNotifications.forEach(id => newSet.add(id));
							return newSet;
						});
						updateNotificationCount(0);
					}
				});
			}
		}
	}, [data?.pages?.[0]]);

	if (isError) {
		return (
			<div className="container notifications-container">
				<h1 className="notifications-title">Bildirimler</h1>
				<div className="notifications-error">Bildirimler yüklenirken bir hata oluştu.</div>
			</div>
		);
	}

	return (
		<div className="container notifications-container">
			<h1 className="notifications-title">Bildirimler</h1>
			{allNotifications.length === 0 && (
				<div className="notifications-empty">
					<div className="empty-icon">
						<img
							src={BellOff}
							className="icons-bell-off"
						/>
					</div>
					<p className="empty-message">Şu anda herhangi bir bildiriminiz bulunmamaktadır.</p>
				</div>
			)}

			{allNotifications.length > 0 && (
				<div className="notifications-list">
					{allNotifications.map((data, index) => {
						const isRead = data?.readDate || readNotificationIds.has(data?.id);

						return (
							<div
								key={`${data.id}-${index}`}
								className="notification-item">
								<div className="timeline-wrapper">
									<div className={`notification-date ${isRead ? 'inactive' : 'active'}`}>{formatCustomDate(data?.notificationCreatedDate)}</div>
									<div className="timeline-element">
										<div className="line-top"></div>
										<div className={`dot ${isRead ? 'inactive' : 'active'}`}></div>
										<div className="line-bottom"></div>
									</div>
								</div>

								<div className="notification-card-wrapper">
									<div className="notification-card">
										<div className={`notification-avatar ${data?.notification?.notificationType}`}>
											<NotificationIcon type={data?.notification?.notificationType} />
										</div>

										<div className="notification-content">
											{data?.notification?.title && <span className="notification-name">{data?.notification?.title}</span>}
											<span className={'notification-body'}>{data?.notification?.body}</span>
											<p className={'notification-mobile-date'}>{formatCustomDate(data?.notificationCreatedDate)}</p>
										</div>
									</div>
								</div>
							</div>
						);
					})}

					<div
						ref={loadMoreRef}
						className={'notifications-loading-more-container'}>
						{isFetchingNextPage && <div className="notifications-loading-more">Daha fazla bildirim yükleniyor...</div>}
					</div>
				</div>
			)}

			{isLoading && <LoadingSpinner loadingText={'Bildirimler Yükleniyor...'} />}
		</div>
	);
};

export default NotificationsScreen;
